/**
 * ScheduleStep.tsx — step 4 of the VER-128 Create Programme preview: name, schedule, context
 * notes and an optional case-note upload.
 *
 * The file input below reads only `file.name` for display — it never reads file contents and
 * never uploads anywhere. That matches the info banner's promise ("Verbal Wellbeing does not
 * store or access the original file"), and this preview has no backend to send it to anyway.
 * A real implementation needs verbal-consent-audit before it touches an actual upload path.
 */
import React, { useRef, useState } from "react";
import { Box, Grid, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { FrequencyKey, PopulationKey } from "./createProgramme.types";
import { POPULATION_OPTIONS, SUMMARY_COLLECTION_NAME } from "./createProgramme.mock";

const FREQUENCY_OPTIONS: { value: FrequencyKey; label: string }[] = [
    { value: "Weekly", label: "Weekly" },
    { value: "Fortnightly", label: "Fortnightly" },
    { value: "Monthly", label: "Monthly" },
];

type ScheduleStepProps = {
    population: PopulationKey;
    formatTitle: string;
    name: string;
    onNameChange: (value: string) => void;
    startDate: string;
    onStartDateChange: (value: string) => void;
    frequency: FrequencyKey;
    onFrequencyChange: (value: FrequencyKey) => void;
    contextNotes: string;
    onContextNotesChange: (value: string) => void;
    caseNoteFileName: string;
    onCaseNoteFileChange: (fileName: string) => void;
};

const SummaryRow: React.FC<{ label: string; value: string; emphasise?: boolean }> = ({ label, value, emphasise }) => (
    <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "borderShade.main" }}
    >
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography
            variant="body2"
            fontWeight={700}
            color={emphasise ? "primary.main" : "secondary.main"}
            textAlign="right"
        >
            {value}
        </Typography>
    </Stack>
);

export const ScheduleStep: React.FC<ScheduleStepProps> = ({
    population,
    formatTitle,
    name,
    onNameChange,
    startDate,
    onStartDateChange,
    frequency,
    onFrequencyChange,
    contextNotes,
    onContextNotesChange,
    caseNoteFileName,
    onCaseNoteFileChange,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);
    const populationLabel = POPULATION_OPTIONS.find((option) => option.key === population)?.title ?? "";

    return (
        <Stack spacing={3.5}>
            <Stack spacing={1}>
                <Typography variant="overline" color="primary.main">
                    Step 4 of 4
                </Typography>
                <Typography variant="h3" component="h2" color="secondary.main">
                    Name and schedule your programme
                </Typography>
                <Typography variant="body1" color="secondary.dark" sx={{ lineHeight: 1.6 }}>
                    A few optional details to help you track this programme in your dashboard.
                </Typography>
            </Stack>

            <Grid container spacing={3.5}>
                <Grid item xs={12} md={8}>
                    <Stack spacing={2.75}>
                        <TextField
                            label="Programme name (optional — we'll generate one if you skip)"
                            placeholder="e.g. HMP Maghaberry Cohort 3 · Spring 2026"
                            value={name}
                            onChange={(e) => onNameChange(e.target.value)}
                            fullWidth
                            helperText="Used only in your dashboard. Participants never see this."
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Delivery start date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => onStartDateChange(e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Session frequency"
                                    value={frequency}
                                    onChange={(e) => onFrequencyChange(e.target.value as FrequencyKey)}
                                    fullWidth
                                >
                                    <MenuItem value="">
                                        <em>Select...</em>
                                    </MenuItem>
                                    {FREQUENCY_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        <TextField
                            label="Context notes (optional)"
                            placeholder="Setting, known sensitivities, previous programme history, safeguarding context..."
                            value={contextNotes}
                            onChange={(e) => onContextNotesChange(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            helperText="Used by the AI to shape the assessment conversation and story preparation. Not shared with participants."
                        />

                        <Stack spacing={1.25}>
                            <Typography variant="body2" color="secondary.main">
                                <Box component="strong" fontWeight={700}>
                                    Upload case notes or pen picture
                                </Box>{" "}
                                <Box component="span" color="text.secondary">
                                    (optional · 1-2-1 only)
                                </Box>
                            </Typography>
                            <Box
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => {
                                    // Without preventDefault the browser navigates to the dropped
                                    // file instead of letting onDrop fire.
                                    e.preventDefault();
                                    setDragActive(true);
                                }}
                                onDragLeave={() => setDragActive(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragActive(false);
                                    // Filename only, exactly as the click path does — the file is
                                    // never read and never uploaded (see the notice below).
                                    onCaseNoteFileChange(e.dataTransfer.files?.[0]?.name ?? "");
                                }}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.5,
                                    py: 3.5,
                                    px: 2.5,
                                    bgcolor: dragActive ? "primary.light" : "background.paper",
                                    border: "1.5px dashed",
                                    borderColor: dragActive ? "primary.main" : "borderShade.main",
                                    borderRadius: 1.5,
                                    cursor: "pointer",
                                    transition: "background-color 160ms ease, border-color 160ms ease",
                                    "&:hover": { borderColor: "primary.main", bgcolor: "primary.light" },
                                }}
                            >
                                <AttachFileOutlinedIcon sx={{ fontSize: 26, color: "text.secondary" }} />
                                <Typography variant="body2" fontWeight={700} color="secondary.main">
                                    {caseNoteFileName || "Drop a file or click to upload"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    PDF, Word or plain text · max 10MB
                                </Typography>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt"
                                    hidden
                                    onChange={(e) => onCaseNoteFileChange(e.target.files?.[0]?.name ?? "")}
                                />
                            </Box>
                            <Stack
                                direction="row"
                                spacing={1.25}
                                sx={{ p: 1.75, borderRadius: 1, bgcolor: "info.light" }}
                            >
                                <LockOutlinedIcon sx={{ fontSize: 20, color: "info.main", flex: "0 0 auto" }} />
                                <Typography variant="body2" color="secondary.main" sx={{ lineHeight: 1.55 }}>
                                    Case notes are processed to generate the assessment profile. Verbal Wellbeing does
                                    not store or access the original file. Only AI-inferred summary data is retained.
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: "secondary.main", color: "common.white" }}>
                            <Typography variant="overline" color="common.white">
                                Your programme
                            </Typography>
                        </Box>
                        <SummaryRow label="Population" value={populationLabel} emphasise />
                        <SummaryRow label="Format" value={formatTitle} />
                        <SummaryRow label="Collection" value={SUMMARY_COLLECTION_NAME} />
                        <SummaryRow label="Start date" value={startDate || "Not set"} />
                        <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                Frequency
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="secondary.main">
                                {frequency || "Not set"}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="caption"
                            sx={{ display: "block", p: 2, bgcolor: "secondaryShade.main", fontWeight: 700 }}
                            color="secondary.dark"
                        >
                            You can edit all of these after the programme is created.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Stack>
    );
};
